import { useId, useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH, type ChangePasswordResult } from '@quds/shared';
import { useAuth } from '@/auth/AuthContext';
import { FormField, Input, fieldDescribedBy } from '@/components/ui';
import { hasErrors, mapFieldIssues, type FieldErrors } from '@/features/pharmacies/validation';
import { ApiError, errorMessage } from '@/lib/api';

interface Values {
  currentPassword: string;
  newPassword: string;
  confirm: string;
}

const EMPTY: Values = { currentPassword: '', newPassword: '', confirm: '' };

/** Client-side checks mirror the API policy; the API verifies the current password and enforces the rules. */
function validate(v: Values, forced: boolean): FieldErrors<Values> {
  const e: FieldErrors<Values> = {};
  if (!v.currentPassword) e.currentPassword = forced ? 'Enter the temporary password you signed in with.' : 'Enter your current password.';
  if (v.newPassword.length < PASSWORD_MIN_LENGTH) e.newPassword = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  else if (v.newPassword === v.currentPassword) e.newPassword = forced ? 'Choose a password different from the temporary one.' : 'Choose a password different from your current one.';
  if (!e.newPassword && v.newPassword !== v.confirm) e.confirm = 'Passwords do not match.';
  return e;
}

interface ChangePasswordFormProps {
  /** Lets an external submit button (e.g. a modal footer) target this form. */
  formId: string;
  /** Completing a change required after account creation or a Super Admin reset. */
  forced?: boolean;
  onSavingChange: (saving: boolean) => void;
  onChanged: (result: ChangePasswordResult) => void;
}

export function ChangePasswordForm({ formId, forced = false, onSavingChange, onChanged }: ChangePasswordFormProps) {
  const { changePassword } = useAuth();
  const base = useId();
  const id = (f: keyof Values) => `${base}-${f}`;
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors<Values>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (p: Partial<Values>) => {
    const next = { ...values, ...p };
    setValues(next);
    if (hasErrors(errors)) setErrors(validate(next, forced));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validate(values, forced);
    setErrors(found);
    if (hasErrors(found)) return;
    onSavingChange(true);
    setSubmitError(null);
    try {
      const result = await changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword });
      onChanged(result);
    } catch (err) {
      if (err instanceof ApiError && err.fieldIssues.length) {
        setErrors(mapFieldIssues<Values>(err.fieldIssues, {}));
      } else {
        setSubmitError(errorMessage(err, 'Could not change the password.'));
      }
      onSavingChange(false);
    }
  };

  return (
    <>
      {submitError && (
        <p role="alert" className="mb-4 rounded-md border border-danger/20 bg-danger-soft px-3 py-2 text-[13px] text-danger">
          {submitError}
        </p>
      )}
      <form id={formId} noValidate onSubmit={onSubmit} className="grid gap-4">
        <FormField label={forced ? 'Temporary Password' : 'Current Password'} htmlFor={id('currentPassword')} required error={errors.currentPassword}>
          <Input
            id={id('currentPassword')}
            type="password"
            autoComplete="current-password"
            value={values.currentPassword}
            onChange={(e) => patch({ currentPassword: e.target.value })}
            invalid={!!errors.currentPassword}
            aria-describedby={fieldDescribedBy(id('currentPassword'), { error: errors.currentPassword })}
            autoFocus
          />
        </FormField>
        <FormField label="New Password" htmlFor={id('newPassword')} required error={errors.newPassword} hint={`Minimum ${PASSWORD_MIN_LENGTH} characters.`}>
          <Input
            id={id('newPassword')}
            type="password"
            autoComplete="new-password"
            value={values.newPassword}
            onChange={(e) => patch({ newPassword: e.target.value })}
            invalid={!!errors.newPassword}
            aria-describedby={fieldDescribedBy(id('newPassword'), { hint: true, error: errors.newPassword })}
          />
        </FormField>
        <FormField label="Confirm New Password" htmlFor={id('confirm')} required error={errors.confirm}>
          <Input
            id={id('confirm')}
            type="password"
            autoComplete="new-password"
            value={values.confirm}
            onChange={(e) => patch({ confirm: e.target.value })}
            invalid={!!errors.confirm}
            aria-describedby={fieldDescribedBy(id('confirm'), { error: errors.confirm })}
          />
        </FormField>
      </form>
    </>
  );
}
